import React, { useState } from "react";
import { useTheme } from "@mui/material/styles";
import { isEmpty } from "lodash";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { formatearMonto } from "../../../helpers/formato";
import {
  descripcionGrafico,
  escalasGrafico,
  formatearMontoCompacto,
  paletaGraficos,
  pluginsGrafico,
} from "../../../theme/graficos";
import { useDatoPanel } from "../useDatoPanel";
import { alturaBarras, ordenarDeMayorAMenor } from "../panel";
import SeccionPanel from "../SeccionPanel";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/**
 * Monto por gestor en barras horizontales, de mayor a menor: el nombre completo se lee en el eje
 * (en vertical había que recortarlo) y el primero de la lista es el que más pesa.
 *
 * Sin total junto al título: el servicio solo cuenta a los gestores (rol 19), así que la suma no
 * coincide con los indicadores de Recuperación Diaria y Mora Total, y parecería un error.
 *
 * @param {object} props
 * @param {string} props.titulo
 * @param {Function} props.obtener Función de `api/Dashboard`; responde `[{ gestor, montoCobrado }]`.
 * @param {"exito"|"error"} props.color Color con significado de la paleta: éxito lo cobrado, error la mora.
 * @param {string} props.descripcionVacio
 */
const BarrasPorGestor = ({ titulo, obtener, color, descripcionVacio }) => {
  const [items, setItems] = useState([]);
  const paleta = paletaGraficos(useTheme());

  const { cargando, error, recargar } = useDatoPanel(obtener, (respuesta) =>
    setItems(
      ordenarDeMayorAMenor(respuesta.result, (i) => i.montoCobrado).map((i) => ({
        gestor: String(i.gestor ?? "").trim(),
        monto: i.montoCobrado,
      }))
    )
  );

  const dataSet = {
    labels: items.map((i) => i.gestor),
    datasets: [
      {
        data: items.map((i) => i.monto),
        backgroundColor: paleta[color],
        borderRadius: 6,
        maxBarThickness: 28,
      },
    ],
  };

  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: pluginsGrafico(paleta, {
      etiqueta: (context) =>
        context.parsed.x !== null ? formatearMonto(context.parsed.x) : "",
    }),
    // Marcas del eje compactas (C$600k); la burbuja sigue con el monto completo.
    scales: escalasGrafico(paleta, formatearMontoCompacto, { horizontal: true }),
  };

  return (
    <SeccionPanel
      titulo={titulo}
      cargando={cargando}
      error={error}
      vacio={isEmpty(items)}
      descripcionVacio={descripcionVacio}
      onReintentar={recargar}
      altura={alturaBarras(items.length)}
    >
      <Bar
        data={dataSet}
        options={options}
        aria-label={descripcionGrafico(titulo, dataSet, formatearMonto)}
      />
    </SeccionPanel>
  );
};

export default BarrasPorGestor;
